import { useEffect, useState } from "react";
import {
  Box,
  SimpleGrid,
  Card,
  CardBody,
  Text,
  Badge,
  VStack,
  HStack,
  Spinner,
  Center,
} from "@chakra-ui/react";

export default function CollectionCards({
  definitions,
  selectedCollection,
  onCollectionSelect,
}) {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const response = await fetch("https://codex.mrc.ai/api/codelists/CODIFY003");
        const data = await response.json();

        // Calculate count for each collection
        const collectionCounts = definitions.reduce((acc, def) => {
          if (def.collection) {
            acc[def.collection] = (acc[def.collection] || 0) + 1;
          }
          return acc;
        }, {});

        // Map collections with their counts (using longCode as the key)
        const collectionsWithCounts = data.codes
          .filter((item) => item.longCode) // Only include items with longCode
          .map((item) => ({
            code: item.longCode, // Use longCode as the identifier
            description: item.description1 || item.longCode,
            count: collectionCounts[item.longCode] || 0,
          }));

        // Sort by count (descending) and then by description
        collectionsWithCounts.sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return a.description.localeCompare(b.description);
        });

        setCollections(collectionsWithCounts);
      } catch (error) {
        console.error("Failed to fetch collections:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCollections();
  }, [definitions]);

  if (loading) {
    return (
      <Center p={4}>
        <Spinner size="md" />
      </Center>
    );
  }

  const totalCount = definitions.length;

  return (
    <Box>
      <SimpleGrid columns={{ base: 2, md: 4, lg: 6, xl: 12 }} spacing={3}>
        {/* "All" card */}
        <Card
          cursor="pointer"
          onClick={() => onCollectionSelect(null)}
          bg={selectedCollection === null ? "blue.500" : "white"}
          color={selectedCollection === null ? "white" : "gray.800"}
          borderWidth="2px"
          borderColor={selectedCollection === null ? "blue.600" : "gray.200"}
          _hover={{
            borderColor: selectedCollection === null ? "blue.700" : "blue.300",
            transform: "translateY(-2px)",
            shadow: "md",
          }}
          transition="all 0.2s"
        >
          <CardBody p={3}>
            <VStack align="stretch" spacing={2}>
              <HStack justify="space-between">
                <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                  All Collections
                </Text>
                <Badge
                  colorScheme={selectedCollection === null ? "whiteAlpha" : "blue"}
                  fontSize="xs"
                >
                  {totalCount}
                </Badge>
              </HStack>
              <Text fontSize="xs" opacity={0.8}>
                View all definitions
              </Text>
            </VStack>
          </CardBody>
        </Card>

        {/* Individual collection cards */}
        {collections.map((collection) => {
          const hasDefinitions = collection.count > 0;
          const isSelected = selectedCollection === collection.code;

          return (
            <Card
              key={collection.code}
              cursor="pointer"
              onClick={() => onCollectionSelect(collection.code)}
              bg={isSelected ? "blue.500" : "white"}
              color={isSelected ? "white" : "gray.800"}
              borderWidth="2px"
              borderColor={isSelected ? "blue.600" : "gray.200"}
              opacity={hasDefinitions ? 1 : 0.6}
              _hover={{
                borderColor: isSelected ? "blue.700" : "blue.300",
                transform: "translateY(-2px)",
                shadow: "md",
                opacity: 1,
              }}
              transition="all 0.2s"
            >
              <CardBody p={3}>
                <VStack align="stretch" spacing={2}>
                  <HStack justify="space-between">
                    <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                      {collection.description}
                    </Text>
                    <Badge
                      colorScheme={
                        isSelected ? "whiteAlpha" : hasDefinitions ? "blue" : "gray"
                      }
                      fontSize="xs"
                    >
                      {collection.count}
                    </Badge>
                  </HStack>
                  <Text fontSize="xs" opacity={0.8} noOfLines={2}>
                    {collection.code}
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          );
        })}
      </SimpleGrid>
    </Box>
  );
}
